import { isPadeDomain } from "utils/utils"
import { fadeInWhenCompact, fadeOutWhenCompact } from "./NavTab"

type LogoProps = {
  src?: string
  alt?: string
}

/** Square white mark shown in the collapsed rail (also the app favicon). */
const COLLAPSED_MARK = `${process.env.PUBLIC_URL ?? ""}/Logo.svg`

/**
 * The mark that stands in for the logo when the rail is collapsed: the app
 * mark on the Pade domain, otherwise the first letter of the organisation
 * name, falling back to the app mark while the name is unavailable.
 */
export const BrandMark = ({
  alt = "",
  name,
}: {
  alt?: string
  name?: string
}) => {
  const initial = isPadeDomain()
    ? undefined
    : name?.trim().charAt(0).toUpperCase()

  return (
    <div className="flex h-10 w-10 items-center justify-center">
      {initial ? (
        <span
          aria-label={name}
          className="text-a11y text-lg font-semibold leading-none"
        >
          {initial}
        </span>
      ) : (
        <img
          src={COLLAPSED_MARK}
          alt={alt}
          className="h-7 w-7 object-contain"
        />
      )}
    </div>
  )
}

/**
 * Brand block at the top of the non-admin sidebars. The full-width logo and
 * the rail-sized mark sit in grid rows that trade 1fr/0fr, so the block's
 * height glides between the two instead of jumping (same trick as
 * .sidebar-submenu).
 */
export const BrandLogoBlock = ({
  src,
  alt = "",
  compact,
  orgName,
}: LogoProps & { compact: boolean; orgName?: string }) => (
  <div
    className={`grid shrink-0 overflow-hidden transition-[grid-template-rows,padding,margin] duration-300 ease-in-out motion-reduce:transition-none ${
      compact ? "mb-3 mt-0 pl-0" : "mb-6 mt-2 pl-4"
    }`}
    style={{ gridTemplateRows: compact ? "0fr 1fr" : "1fr 0fr" }}
  >
    <div
      aria-hidden={compact}
      className={`min-h-0 overflow-hidden ${fadeOutWhenCompact(compact)}`}
    >
      <img
        src={src}
        alt={alt}
        className="w-[100px]"
      />
    </div>
    <div
      aria-hidden={!compact}
      className={`min-h-0 overflow-hidden ${fadeInWhenCompact(compact)}`}
    >
      <div className="w-fit rounded-md bg-primary600 shadow">
        <BrandMark
          alt={alt}
          name={orgName}
        />
      </div>
    </div>
  </div>
)
